import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import c from 'classnames';

import { KeyCodes } from '../consts/keyCodes';
import Input from '../Input';
import { getInputTypographyVariant } from '../Input/utils';
import { eventPreventDefault, eventStopPropagation } from '../utils/events';
import useNumberInput from './hooks/useNumberInput';
import { renderNumberInputEndAdornment } from './renderers';
import { numberInputStyles } from './styles';
import { NumberInputProps } from './types';
import { setInputAutoWidth } from './utils';

const NAN = Number.NaN;
const ignoreKeys: string[] = [
  KeyCodes.Period,
  KeyCodes.Plus,
  KeyCodes.Minus,
  KeyCodes.E,
  KeyCodes.e,
];

function NumberInput({
  canBeEmpty = false,
  className = 'w-96',
  endIcon,
  error,
  inputProps,
  inputSuffix,
  max,
  min,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  onEnter,
  showControls = true,
  size = 'large',
  step = 1,
  value: valueProp,
  variant,
  ...rest
}: NumberInputProps) {
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const { onBlur, onChange, onDecrement, onIncrement, value } = useNumberInput({
    canBeEmpty,
    max,
    min,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    step,
    value: valueProp,
  });

  const resolvedInputProps = useMemo(() => {
    return {
      max,
      min,
      step,
      style: inputSuffix ? { minWidth: '1ch', width: '0px' } : {},
      onWheel: (e: React.WheelEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        e.currentTarget.blur(),
      ...inputProps,
    };
  }, [min, max, step, inputSuffix, inputProps]);

  const onClick = useCallback(() => {
    inputRef?.focus();
  }, [inputRef]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (ignoreKeys.includes(event.key)) {
        eventPreventDefault(event);
        eventStopPropagation(event);
      }

      if (event.key === KeyCodes.Enter) {
        onEnter?.();
      }
    },
    [onEnter],
  );

  // TODO: add for backward compatibility, should we handle this at app level?
  // might want show error on submit along with other form fields
  const hasError = useMemo(() => {
    const minValue = min ?? NAN;
    const maxValue = max ?? NAN;

    return valueProp < minValue || valueProp > maxValue;
  }, [max, min, valueProp]);

  useLayoutEffect(() => {
    if (inputSuffix && inputRef) {
      setInputAutoWidth(inputRef);
    }
  }, [inputRef, inputSuffix, value]);

  return (
    <Input
      className={c(className, numberInputStyles)}
      endAdornment={renderNumberInputEndAdornment({
        endIcon,
        inputSuffix,
        onDecrease: onDecrement,
        onIncrease: onIncrement,
        showControls,
        variant: getInputTypographyVariant({ size, variant }),
      })}
      error={error || hasError}
      inputProps={resolvedInputProps}
      inputRef={setInputRef}
      size={size}
      type="number"
      value={value}
      variant={variant}
      onBlur={onBlur}
      onChange={onChange}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...rest}
    />
  );
}

export default React.memo(NumberInput);
